import { useState, useCallback, FormEvent } from 'react';
import { Helmet } from 'react-helmet-async';
import { DarkChapter } from '@/components/layout/DarkChapter';
import { SimpleReveal } from '@/components/SimpleReveal';
import { AudioWaveform } from '@/components/AudioWaveform';
import { Navigation } from '@/components/Navigation';
import { Footer } from '@/components/Footer';
import { Loader2, Mail } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { trackLead } from '@/lib/metaPixel';

const PAGE_TITLE = 'Get Free Stuff | DK Artists';
const PAGE_DESCRIPTION = 'Join the DK Artists mailing list for free resources for independent artists.';
const CANONICAL_URL = 'https://dk-artists.com/mailing-list';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function MailingList() {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const handleSubmit = useCallback(async (e: FormEvent) => {
    e.preventDefault();
    const trimmed = email.trim();

    if (!EMAIL_REGEX.test(trimmed)) {
      setStatus('error');
      setErrorMessage('Please enter a valid email address.');
      return;
    }

    setStatus('loading');
    setErrorMessage('');

    try {
      const { data, error } = await supabase.functions.invoke('join-mailing-list', {
        body: { email: trimmed },
      });

      if (error) throw error;
      if (data && data.ok === false) throw new Error(data.error || 'Signup failed');

      setStatus('success');
      trackLead();
    } catch (err) {
      console.error('Mailing list signup failed:', err);
      setStatus('error');
      setErrorMessage('Something went wrong. Please try again.');
    }
  }, [email]);

  return (
    <>
      <Helmet>
        <title>{PAGE_TITLE}</title>
        <meta name="description" content={PAGE_DESCRIPTION} />
        <meta name="robots" content="noindex" />
        <link rel="canonical" href={CANONICAL_URL} />
        <meta property="og:title" content={PAGE_TITLE} />
        <meta property="og:description" content={PAGE_DESCRIPTION} />
        <meta property="og:url" content={CANONICAL_URL} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={PAGE_TITLE} />
        <meta name="twitter:description" content={PAGE_DESCRIPTION} />
      </Helmet>

      <Navigation />

      <DarkChapter>
        <main className="relative z-10">
          {/* Hero */}
          <section className="px-6 lg:px-12 pt-24 pb-6 md:pt-32 md:pb-8">
            <div className="max-w-5xl mx-auto text-center">
              <SimpleReveal>
                <div className="mb-4 text-center flex flex-col items-center">
                  <AudioWaveform />
                  <div className="inline-flex items-center bg-white/5 border border-white/10 rounded-md px-2 lg:px-2.5 py-1 lg:py-1.5 gap-2">
                    <span className="font-mono text-[10px] lg:text-xs text-white/50 tracking-[0.15em] uppercase">
                      &gt; THE ARTIST ENGINE
                    </span>
                  </div>
                </div>
              </SimpleReveal>

              <SimpleReveal delay={80}>
              <h1 className="font-headline font-bold text-white text-[clamp(2.5rem,7vw,5rem)] leading-[1.05] tracking-[-0.03em] mb-4">
                Get{' '}
                <span className="inline-block bg-[#f5f5f7] text-[#1d1d1f] px-3 py-1 rounded-lg font-bold -rotate-2 shadow-[0_0_40px_rgba(0,113,227,0.7)]">
                  Free Stuff
                </span>
                <br />
                On The Mailing List
              </h1>
              </SimpleReveal>

              <SimpleReveal delay={160}>
                <p className="text-lg md:text-xl text-[#0071e3] font-semibold leading-relaxed">
                  Enter Your Email Below
                </p>
              </SimpleReveal>
            </div>
          </section>

          {/* Signup Form */}
          <section className="px-6 lg:px-12 py-6 md:py-8">
            <div className="max-w-xl mx-auto">
              <SimpleReveal delay={200}>
                {status === 'success' ? (
                  <div className="rounded-xl border border-white/[0.08] bg-white/[0.02] p-8 md:p-10 text-center">
                    <h2 className="font-headline font-bold text-white text-2xl mb-2">
                      Welcome.
                    </h2>
                    <p className="text-white/60 text-base leading-relaxed">
                      And congratulations for joining. Next time an email leaves my inbox, it'll be in yours :)
                    </p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} noValidate>
                    <div className="rounded-xl border border-white/[0.08] bg-white/[0.02] p-5 md:p-6">
                      <label
                        htmlFor="mailing-list-email"
                        className="block font-mono text-[10px] lg:text-xs text-white/40 tracking-[0.15em] uppercase mb-3"
                      >
                        Email Address
                      </label>
                      <input
                        id="mailing-list-email"
                        type="email"
                        inputMode="email"
                        autoComplete="email"
                        placeholder="you@example.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        disabled={status === 'loading'}
                        className="
                          w-full h-14 px-4 mb-4 rounded-xl
                          bg-white/[0.04] border border-white/10
                          text-white text-base placeholder:text-white/25
                          transition-colors duration-200
                          focus:outline-none focus:border-[#0071e3]/60 focus:bg-white/[0.06]
                          disabled:opacity-50
                        "
                      />
                      <button
                        type="submit"
                        disabled={status === 'loading'}
                        className="
                          inline-flex items-center justify-center gap-2 whitespace-nowrap
                          w-full min-h-[56px] px-8 py-4 text-base font-semibold rounded-xl
                          transition-all duration-300 ease-out
                          bg-white text-[#1d1d1f]
                          hover:-translate-y-0.5 hover:shadow-[0_8px_24px_rgba(255,255,255,0.15)]
                          active:translate-y-0 active:shadow-none
                          focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-[#050505]
                          disabled:opacity-60 disabled:pointer-events-none
                        "
                      >
                        {status === 'loading' ? (
                          <>
                            <Loader2 className="w-5 h-5 animate-spin" aria-hidden="true" />
                            <span>JOINING...</span>
                          </>
                        ) : (
                          <>
                            <Mail className="w-5 h-5" aria-hidden="true" />
                            <span>JOIN THE MAILING LIST</span>
                          </>
                        )}
                      </button>
                      <div
                        className={`
                          mt-3 text-sm text-center transition-all duration-300
                          ${status === 'error' ? 'opacity-100 translate-y-0 text-red-400' : 'opacity-0 -translate-y-1 pointer-events-none text-white/60'}
                        `}
                        aria-live="polite"
                      >
                        {errorMessage || ' '}
                      </div>
                    </div>
                  </form>
                )}
              </SimpleReveal>
            </div>
          </section>
        </main>

        <Footer />
      </DarkChapter>
    </>
  );
}
